import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { ReviewSessionDetails } from './ReviewSessionDetails';
import { feedbackSessionQuestion } from '../../actions/feedbackQuestionAction';
import { feedbackSessionFormInFocus } from '../../actions/feedbackFormInFocusAction';
import { ProgramQuestionInterface, SessionFeedbackAnswerInterface } from './SessionFeedback';
import { LoggedInUserDetails } from '../../reducers';
import { resetSessionFeedbackForm } from '../../actions/feedbackAnswerAction';
import { useSubmitOfflineFeedbackMutation, useSubmitSessionFeedbackMutation } from '../../store/api/feedbackApi';
import moment from 'moment';
import { omit } from 'lodash';
import { trackMixpanelEvent } from '../../services/mixpanel';

interface SessionSummaryProps {
	programQuestion: ProgramQuestionInterface[];
	programAnswer: SessionFeedbackAnswerInterface;
	loggedInUserDetail: LoggedInUserDetails;
}

export default function SessionSummary({ programQuestion, programAnswer, loggedInUserDetail }: SessionSummaryProps) {
	const { sessionDetailForm, sessionFeedbackForm } = programAnswer;
	const { sessionWith, sessionService } = sessionDetailForm;
	const { mentee, mentor, group_id } = sessionWith.value;
	const formType = mentee.id !== loggedInUserDetail.id ? 'mentee' : 'mentor';
	const [value, setValue] = useState('1');
	const [isSubmitted, setIsSubmitted] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [submitOfflineFeedback] = useSubmitOfflineFeedbackMutation();
	const [submitSessionFeedback] = useSubmitSessionFeedbackMutation();

	const handleTabChange = async () => {
		if (value === '1') {
			setValue('2');
			return;
		}
		const formData = new FormData();
		if (sessionDetailForm.bookingId) {
			const feedbackFormAnswer = sessionFeedbackForm.feedback.map((answer) => omit(answer, ['required', 'question_category']));
			formData.append('booking_reviews', JSON.stringify(feedbackFormAnswer));
			await submitSessionFeedback({ bookingId: sessionDetailForm.bookingId, formData }).unwrap();
			trackMixpanelEvent('Submitted Pending Session', {
				'User Id': loggedInUserDetail.id,
				'Review Form Type': formType,
				'Booking Id': sessionDetailForm.bookingId,
				'Program Name': sessionDetailForm.programme.label,
				'Program Id': sessionDetailForm.programme.value,
				'Session With': sessionDetailForm.sessionWith.label,
				'Group Id': sessionDetailForm.sessionWith.value.group_id,
				'Service Provided': sessionDetailForm.sessionService.label,
				'Session Type': sessionDetailForm.sessionType === 0 ? 'Online' : 'Physical',
			});
		} else {
			const feedbackFormAnswer = sessionFeedbackForm.feedback.map((answer) => omit(answer, ['required', 'question_category', 'rated_participant_id']));
			formData.append('program_id', `${sessionDetailForm.programme.value}`);
			formData.append('time', `${moment(sessionDetailForm.sessionTime).format('HHmm')}`);
			formData.append('date', `${moment(sessionDetailForm.sessionDate).format('DD/MM/YYYY')}`);
			formData.append('mentee_id', `${mentee.id}`);
			formData.append('mentor_id', `${mentor.id}`);
			formData.append('session_method', `${sessionDetailForm.sessionType}`);
			formData.append('group_id', `${group_id}`);
			formData.append('review_form_for', formType);
			formData.append('service_id', `${sessionService.value}`);
			formData.append('feedback', JSON.stringify(feedbackFormAnswer));
			await submitOfflineFeedback(formData).unwrap();
			trackMixpanelEvent('Submitted New Offline Session', {
				'User Id': loggedInUserDetail.id,
				'Review Form Type': formType,
				'Program Name': sessionDetailForm.programme.label,
				'Program Id': sessionDetailForm.programme.value,
				'Session With': sessionDetailForm.sessionWith.label,
				'Group Id': sessionDetailForm.sessionWith.value.group_id,
				'Service Provided': sessionDetailForm.sessionService.label,
				'Session Type': sessionDetailForm.sessionType === 0 ? 'Online' : 'Physical',
			});
		}
		setIsSubmitted(true);
	};

	const routeToHomePage = () => {
		dispatch(feedbackSessionFormInFocus(null));
		dispatch(feedbackSessionQuestion([]));
		dispatch(resetSessionFeedbackForm());
		navigate('..');
	};
	const routeToPreviousSessionPage = () => {
		dispatch(feedbackSessionFormInFocus(null));
		dispatch(feedbackSessionQuestion([]));
		dispatch(resetSessionFeedbackForm());
		navigate('../previous-session');
	};
	const handleEdit = () => {
		if (value === '2') {
			dispatch(feedbackSessionFormInFocus(2));
			trackMixpanelEvent('Edit Session Feedback Form');
		}
	};

	if (isSubmitted) {
		return (
			<div className="mx-auto flex max-w-xl flex-col items-center px-4 pt-16 text-center">
				<img alt="" className="size-56" src='src="../../../feedback-submitted.png' />
				<h1 className="py-3 text-xl text-gray-900">Congratulations!</h1>
				<p className="py-2 text-base text-gray-700">Your form has been submitted. Thank you for your feedback and ratings</p>
				<Button className="mt-4 w-56" onClick={routeToHomePage}>OK</Button>
			</div>
		);
	}

	const primaryAction = programAnswer.sessionFeedbackForm.submitted
		? value === '2'
			? <Button className="w-full" onClick={routeToPreviousSessionPage}>Back</Button>
			: <Button className="w-full" onClick={handleTabChange}>Next</Button>
		: <div className="flex w-full gap-2 [&>*]:flex-1 md:w-auto md:[&>*]:flex-none">{value === '2' && <Button onClick={handleEdit} variant="outline">Edit</Button>}<Button onClick={handleTabChange}>{value === '2' ? 'Submit' : 'Next'}</Button></div>;

	return (
		<div className="w-full">
			<header className="flex items-center border-b border-gray-200 pb-5 pt-6">
				<div className="session-feedback flex-1 pl-4">Summary</div>
				<div className="hidden pr-4 md:block">{primaryAction}</div>
			</header>
			<Tabs value={value}>
				<TabsList className="h-auto w-full rounded-none border-b border-gray-200 bg-transparent p-0 pt-2" variant="line">
					<TabsTrigger className="required-session py-3" value="1">Session Details</TabsTrigger>
					<TabsTrigger className="submitted-session py-3" value="2">Feedback</TabsTrigger>
				</TabsList>
				<TabsContent className="h-[calc(100dvh-201px)] overflow-y-auto px-4 py-6 md:h-[calc(100dvh-216px)]" value="1">
					<ReviewSessionDetails programAnswer={programAnswer} programQuestion={programQuestion} value={value} />
					<Button className="mt-6 w-full md:hidden" onClick={handleTabChange}>Next</Button>
				</TabsContent>
				<TabsContent className="h-[calc(100dvh-201px)] overflow-y-auto px-4 py-6 md:h-[calc(100dvh-216px)]" value="2">
					<ReviewSessionDetails programAnswer={programAnswer} programQuestion={programQuestion} value={value} />
					<div className="mt-6 md:hidden">{primaryAction}</div>
				</TabsContent>
			</Tabs>
		</div>
	);
}
